import { memo, useCallback, useEffect, useRef, useState } from "react";
import { IntlShape, useIntl } from "react-intl";
import { FieldInputProps, FieldMetaProps, useFormikContext } from "formik";
import { Box, ClassNameMap, Grid, TextField, Typography } from "@mui/material";
import AppButton from "../../components/AppButton";

import {
  CalculEditorFormDetails,
  CalculEditorSimulationFormDetails,
  ConfigurationTableModel,
  editorInputsType,
} from "../interfaces";
import { TooltipLabel } from "../../components/Tooltips";
import { useGetWatchedFieldValue } from "../utils";

/**
 * Use to translate variables in input but return the value to backend
 *
 * @param value Input string value
 * @param filter Input params
 * @param intl intl to translate
 * @param revert Use to convert value => label (true) or label => value (false)
 */
export const transformValue = (
  value: string,
  filter:
    | CalculEditorFormDetails
    | CalculEditorSimulationFormDetails
    | ConfigurationTableModel["attributes"][0],
  intl: IntlShape,
  revert: boolean
) => {
  if (filter.editorInputs) {
    let newValue = value;
    filter.editorInputs
      .filter((button) => button.isVariable)
      .forEach((variable) => {
        return (newValue = newValue.replaceAll(
          revert ? intl.formatMessage({ id: variable.label }) : variable.value,
          revert ? variable.value : intl.formatMessage({ id: variable.label })
        ));
      });

    return newValue;
  }

  return value;
};

const AutoFilledButton = ({
  button,
  label,
  insertAtCursor,
  onWarning,
  isWarningActivated,
}: {
  button: any;
  label: string;
  insertAtCursor: (
    button: editorInputsType,
    cursorOffset?: number,
    variableValue?: number
  ) => void;
  onWarning: (label: string | null) => void;
  isWarningActivated: string | null;
}) => {
  const intl = useIntl();
  const { values } = useFormikContext<any>();
  const watchedFieldValue = useGetWatchedFieldValue(
    values,
    button.key,
    button.watchField
  );

  // Activate warning message on parent if conditions are respected
  useEffect(() => {
    if (watchedFieldValue && button.warning) {
      // If the warning value is null, we check if property exist with hasOwnProperty
      if (
        button.warning.value === null &&
        watchedFieldValue.hasOwnProperty(button.warning.watchField) &&
        (watchedFieldValue[button.warning.watchField] === null ||
          watchedFieldValue[button.warning.watchField] === undefined)
      ) {
        onWarning(intl.formatMessage({ id: button.warning.label }));
      } else if (isWarningActivated) {
        onWarning(null);
      }
    }
  }, [watchedFieldValue, button.warning, intl, onWarning, isWarningActivated]);

  return (
    <AppButton
      intent="tonal"
      sx={{ height: "100%" }}
      onMouseDown={(e) => {
        e.preventDefault();
        insertAtCursor(
          button,
          button.cursorInside ? button.value.indexOf("(") + 1 : undefined,
          watchedFieldValue && watchedFieldValue[button.key]
            ? watchedFieldValue[button.key]
            : undefined
        );
      }}
    >
      <TooltipLabel
        label={
          watchedFieldValue && watchedFieldValue[button.key]
            ? `${label} (${watchedFieldValue[button.key]})`
            : label
        }
        tooltipLabel={intl.formatMessage(
          { id: button.tooltip },
          { br: <br /> }
        )}
        sxStyles={{
          "& svg": { fontSize: "0.938rem" },
        }}
        translateTooltip={false}
      />
    </AppButton>
  );
};

const CalculatorInputs = memo(
  ({
    filter,
    insertAtCursor,
  }: {
    filter: CalculEditorFormDetails;
    insertAtCursor: (
      button: editorInputsType,
      cursorOffset?: number,
      variableValue?: number
    ) => void;
  }) => {
    const intl = useIntl();
    const [warning, setWarning] = useState<string | null>(null);

    const handleWarning = useCallback(
      (label: string | null) => setWarning(label),
      [setWarning]
    );

    return (
      <>
        <Grid
          container
          spacing={1}
          sx={(theme) => ({
            padding: 1,
            borderLeft: `1px solid ${theme.vars.palette.divider}`,
            borderRight: `1px solid ${theme.vars.palette.divider}`,
            borderBottom: `1px solid ${theme.vars.palette.divider}`,
          })}
        >
          {filter.editorInputs?.map((button) => {
            const label = button.translate
              ? intl.formatMessage({ id: button.label })
              : button.label;

            if (button?.type === "autofill") {
              return (
                <Grid size={"auto"} key={button.value}>
                  <AutoFilledButton
                    button={button}
                    label={label}
                    insertAtCursor={insertAtCursor}
                    onWarning={handleWarning}
                    isWarningActivated={warning}
                  />
                </Grid>
              );
            }

            return (
              <Grid size={"auto"} key={button.value}>
                <AppButton
                  intent="tonal"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertAtCursor(
                      button,
                      button.cursorInside
                        ? button.value.indexOf("(") + 1
                        : undefined
                    );
                  }}
                >
                  {label}
                </AppButton>
              </Grid>
            );
          })}
        </Grid>
        <Typography
          sx={(theme) => ({
            color: theme.vars.palette.warning.main,
            fontSize: "0.75rem",
            margin: "4px 14px 0",
          })}
        >
          {warning}
        </Typography>
      </>
    );
  }
);

const CalculEditorInput = ({
  classes,
  field,
  filter,
  meta,
}: {
  classes: ClassNameMap;
  field: FieldInputProps<any>;
  filter: CalculEditorFormDetails;
  meta: FieldMetaProps<any>;
}) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { setFieldValue } = useFormikContext<any>();

  const insertAtCursor = useCallback(
    (button: editorInputsType, cursorOffset?: number) => {
      const value = field.value ?? "";
      const valueDisplayed = button.isVariable
        ? intl.formatMessage({ id: button.label })
        : button.value;

      if (
        filter.inputMaxLength &&
        (value.length >= filter.inputMaxLength ||
          value.length + valueDisplayed.length >= filter.inputMaxLength)
      )
        return;

      const input = inputRef.current;
      if (!input) return;

      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const newValue =
        input.value.slice(0, start) + valueDisplayed + input.value.slice(end);

      setFieldValue(field.name, transformValue(newValue, filter, intl, true));

      requestAnimationFrame(() => {
        const pos = start + (cursorOffset ?? valueDisplayed.length);
        input.setSelectionRange(pos, pos);
      });
    },
    [field.value, field.name, filter, intl, setFieldValue]
  );

  return (
    <Box>
      <TextField
        {...field}
        inputRef={inputRef}
        slotProps={{
          htmlInput: {
            maxLength: filter.inputMaxLength,
          },
        }}
        className={classes.inputText}
        variant="outlined"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          setFieldValue(
            field.name,
            transformValue(e.target.value ?? "", filter, intl, true)
          );
        }}
        label={
          filter.tooltip ? (
            <TooltipLabel
              label={intl.formatMessage({ id: filter.label })}
              tooltipLabel={intl.formatMessage(
                { id: filter.tooltip },
                { br: <br /> }
              )}
              translateTooltip={false}
            />
          ) : (
            intl.formatMessage({ id: filter.label })
          )
        }
        placeholder={
          filter.placeholder
            ? intl.formatMessage({ id: filter.placeholder })
            : undefined
        }
        fullWidth
        error={meta.touched && !!meta.error}
        value={transformValue(field.value ?? "", filter, intl, false)}
        sx={{ marginBottom: 0 }}
      />
      <CalculatorInputs filter={filter} insertAtCursor={insertAtCursor} />
      {meta.touched && meta.error && (
        <Typography
          sx={{
            color: "error.main",
            fontSize: "0.75rem",
            margin: "4px 14px 0",
          }}
        >
          {meta.error}
        </Typography>
      )}
    </Box>
  );
};

export default CalculEditorInput;
